// ═════════════════════════════════════════════════════════════════════════════
// Search a SNOMED concept by name and get its code back.
//
// Fully controlled: the parent owns both the term and the code, so there is no
// local copy to drift out of sync. That matters here — NHCX is sent the code and
// the display text as a pair, and a code left over from a previous selection
// alongside freshly typed text would build a misleading FHIR bundle. Typing
// therefore clears the code, and only picking a concept sets it again.
// ═════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState } from "react";
import IpdTextField from "../../ipd/utils/IpdTextField";
import { C } from "../constants";
import SvgIcon from "../SvgIcon";
import { searchSnomedConcepts } from "../snomed-api";

const DEBOUNCE_MS = 500; // matches the doctor console's search debounce
const MIN_QUERY = 2;

const listStyle = {
  background: "#f8f9fa",
  border: `1px solid ${C.border}`,
  borderRadius: "8px",
  marginTop: "6px",
  maxHeight: "220px",
  overflowY: "auto",
};
const optionStyle = {
  display: "block",
  width: "100%",
  textAlign: "left",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  padding: "8px 12px",
  fontSize: "12px",
  color: C.text,
  lineHeight: 1.4,
};
const noteStyle = {
  fontSize: "11px",
  fontWeight: "700",
  color: C.textSub,
  padding: "8px 12px",
  borderBottom: `1px solid ${C.border}`,
};

export default function SnomedSearchField({
  term,
  code,
  semanticTag,
  placeholder,
  onTermChange,
  onSelect,
}) {
  const [options, setOptions] = useState([]);
  const [fromSuggest, setFromSuggest] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [focused, setFocused] = useState(false);

  // Responses can land out of order once the debounce lets two searches overlap;
  // only the newest one is allowed to write state.
  const requestId = useRef(0);
  const mouseDownInList = useRef(false);

  useEffect(() => {
    // A concept is already selected — its text is the official term, so there is
    // nothing to look up until the user edits it (which clears the code).
    if (code) {
      setOptions([]);
      setSearched(false);
      return undefined;
    }
    const query = (term || "").trim();
    if (query.length < MIN_QUERY) {
      setOptions([]);
      setSearched(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      const id = ++requestId.current;
      setLoading(true);
      try {
        const { concepts, fromSuggest: suggested } = await searchSnomedConcepts(
          query.toLowerCase(),
          semanticTag
        );
        if (id !== requestId.current) return;
        setOptions(concepts);
        setFromSuggest(suggested);
      } catch {
        // A failed lookup must not block the field — the user can still retype.
        if (id !== requestId.current) return;
        setOptions([]);
        setFromSuggest(false);
      } finally {
        if (id === requestId.current) {
          setLoading(false);
          setSearched(true);
        }
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [term, code, semanticTag]);

  const showList = !code && focused && (loading || searched);

  return (
    <div style={{ position: "relative" }}>
      <IpdTextField
        fullWidth
        value={term || ""}
        placeholder={placeholder}
        onChange={(e) => onTermChange(e.target.value)}
        onFocus={() => setFocused(true)}
        // The blur fires before the option's click, so a click inside the list has
        // to keep the list open long enough for that click to register.
        onBlur={() => {
          setTimeout(() => {
            if (!mouseDownInList.current) setFocused(false);
            mouseDownInList.current = false;
          }, 150);
        }}
        InputProps={
          code
            ? {
                endAdornment: (
                  <button
                    type="button"
                    title="Clear and search again"
                    onClick={() => onTermChange("")}
                    style={{ border: "none", background: "transparent", cursor: "pointer", display: "flex", padding: 0 }}
                  >
                    <SvgIcon name="x" size={14} color={C.textSub} />
                  </button>
                ),
              }
            : undefined
        }
      />

      {showList && (
        <div style={listStyle} onMouseDown={() => { mouseDownInList.current = true; }}>
          {loading && <div style={noteStyle}>Searching SNOMED…</div>}

          {!loading && fromSuggest && (
            <div style={{ ...noteStyle, color: C.warning }}>
              No exact match. Search one of these instead:
            </div>
          )}

          {!loading && !fromSuggest && options.length === 0 && (
            <div style={{ ...noteStyle, borderBottom: "none", color: C.danger }}>
              No SNOMED concept found for that name.
            </div>
          )}

          {!loading &&
            options.map((opt) => (
              <button
                key={`${opt.code}-${opt.term}`}
                type="button"
                style={optionStyle}
                // A suggestion carries no code, so it re-runs the search rather than
                // selecting — picking it as a concept would leave the code empty.
                onClick={() => (opt.code ? onSelect(opt) : onTermChange(opt.term))}
                onMouseEnter={(e) => { e.currentTarget.style.background = C.accent; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                {opt.term}
                {opt.code && (
                  <span style={{ color: C.textSub, fontFamily: "monospace", marginLeft: "8px" }}>
                    {opt.code}
                  </span>
                )}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
